/**
 * useEditorCommunication Hook
 *
 * Iframe ↔ Angular postMessage bridge with reinit support.
 *
 * Flow:
 *   1. Mount → listen for postMessage, send READY to Angular
 *   2. Angular sends INIT → hook stores payload, hydrates authStore
 *   3. Consumers read `initPayload` and call APIs directly
 *   4. Hook exposes sendError / sendNavigate for events back to Angular
 *   5. On version navigation, Angular sends a NEW INIT with new entityId
 *      → hook detects changed entityId, bumps `initSequence`, consumers re-init
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import {
  getBridge,
  resetBridge,
} from '@services/communicationBridge.service';
import { authStore } from '@services/authStore.service';
import type {
  InitPayload,
  ErrorPayload,
  NavigatePayload,
  EntityType,
} from '@/types/communication.types';

// =============================================================================
// TYPES
// =============================================================================

export interface UseEditorCommunicationConfig {
  targetOrigin: string;
  debug?: boolean;
}

export interface UseEditorCommunicationReturn {
  /** The INIT payload received from Angular (null until received) */
  initPayload: InitPayload | null;
  /**
   * Monotonically increasing counter — bumps every time a new INIT is accepted.
   * Use as a React dependency to re-run init effects even when entityId is the
   * same (e.g., Angular re-sends INIT after a full page reload inside the iframe).
   */
  initSequence: number;
  /** Send an error event to Angular */
  sendError: (code: string, message: string, details?: unknown) => void;
  /** Send a navigation request to Angular */
  sendNavigate: (entityType: EntityType, entityId: string, entityName?: string) => void;
  /** Send a generic message to Angular */
  sendMessage: (payload: { type: string; payload?: unknown }) => void;
  /** Whether bridge is connected and INIT received */
  isReady: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export function useEditorCommunication(
  config: UseEditorCommunicationConfig
): UseEditorCommunicationReturn {
  const [initPayload, setInitPayload] = useState<InitPayload | null>(null);
  const [initSequence, setInitSequence] = useState(0);
  const lastEntityIdRef = useRef<string | null>(null);

  const lastInitTimestampRef = useRef<number>(0);

  useEffect(() => {
    resetBridge();

    const bridge = getBridge({
      targetOrigin: config.targetOrigin,
      debug: config.debug ?? true,
    });

    bridge.initialize();

    // Register INIT handler — accepts every INIT (reinit on navigation)
    const unregisterInit = bridge.onInit((payload) => {
      // Always refresh auth token
      authStore.setToken(payload.authToken);

      const isNewEntity = payload.entityId !== lastEntityIdRef.current;
      const isFirstInit = lastEntityIdRef.current === null;

      // Deduplicate: same entityId arriving within 1 second is a duplicate
      // (caused by React StrictMode double-effect or Angular safe-pipe re-render).
      // Navigation to a different version always has a different entityId, so
      // the 1-second window never blocks legitimate navigation INITs.
      const now = Date.now();
      const isDuplicate =
        !isFirstInit &&
        !isNewEntity &&
        now - lastInitTimestampRef.current < 1000;

      if (isDuplicate) {
        return;
      }

      lastEntityIdRef.current = payload.entityId;
      lastInitTimestampRef.current = now;

      if (isFirstInit || isNewEntity) {
        setInitPayload(payload);
        setInitSequence((s) => s + 1);
      } else {
        // Same entityId re-sent after the 1-second window (intentional force-reload)
        setInitPayload({ ...payload });
        setInitSequence((s) => s + 1);
      }
    });

    // Tell Angular we're ready to receive INIT
    bridge.sendEvent('READY');

    return () => {
      unregisterInit();
      resetBridge();
      lastEntityIdRef.current = null;
    };
  }, [config.targetOrigin, config.debug]);

  // ---------------------------------------------------------------------------
  // Event senders
  // ---------------------------------------------------------------------------

  const sendError = useCallback(
    (code: string, message: string, details?: unknown) => {
      const bridge = getBridge();
      const payload: ErrorPayload = { code, message, details };
      bridge.sendEvent('ERROR', payload);
    },
    []
  );

  const sendNavigate = useCallback(
    (entityType: EntityType, entityId: string, entityName?: string) => {
      const bridge = getBridge();
      const payload: NavigatePayload = { entityType, entityId, entityName };
      bridge.sendEvent('NAVIGATE', payload);
    },
    []
  );

  const sendMessage = useCallback(
    (msg: { type: string; payload?: unknown }) => {
      const bridge = getBridge();
      bridge.sendEvent(msg.type, msg.payload ?? {});
    },
    []
  );

  return {
    initPayload,
    initSequence,
    sendError,
    sendNavigate,
    sendMessage,
    isReady: initPayload !== null,
  };
}